import { FunctionComponent, useEffect, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { EventPhoto } from '@src/types/images';
import './Gallery.scss';
import { codeToNumber } from '@shared/utils';

interface GalleryProps {
  imageToShow: string;
  images: EventPhoto[];
  isOpen: boolean;
  onClose: (source?: string, rotation?: number) => void;
}

const getGeolocation = (awsUrl: string): string => {
  let refGeolocation = '';
  if (awsUrl.includes('%C2%AC') === true) {
    const splittedURL = awsUrl.split('%C2%AC');
    refGeolocation =
      'LATITUD: ' +
      codeToNumber(splittedURL[1]) +
      ', LONGITUD: ' +
      codeToNumber(splittedURL[2]);
  }

  return refGeolocation;
};

const Gallery: FunctionComponent<GalleryProps> = (props): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(
    props.images.findIndex((i) => i.awsUrl === props.imageToShow),
  );
  const [rotate, setRotate] = useState(0);

  useEffect(() => {
    if (!props.images.length) {
      props.onClose();
    }
  }, [props]);

  useEffect(() => {
    const index = props.images.findIndex((i) => i.awsUrl === props.imageToShow);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  }, [props.imageToShow, props.images]);

  const slides = props.images.map((img) => ({
    src: img.awsUrl || '',
    alt: img.imageName,
    width: 1920,
    height: 1080,
  }));

  const handleRotate = () => {
    const newRotation = (rotate + 90) % 360;
    setRotate(newRotation);
  };

  const handleClose = () => {
    props.onClose(props.images[currentIndex]?.awsUrl || '', rotate);
  };

  return (
    <div className="gallery">
      <Lightbox
        open={props.isOpen}
        close={handleClose}
        slides={slides}
        index={currentIndex}
        plugins={[Zoom]}
        carousel={{ finite: false }}
        controller={{ closeOnBackdropClick: true }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          scrollToZoom: true,
          doubleClickMaxStops: 3,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
        }}
        animation={{
          zoom: 300,
        }}
        on={{
          view: ({ index }) => setCurrentIndex(index),
        }}
        toolbar={{
          buttons: [
            <button
              key="rotate"
              type="button"
              className="yarl__button"
              onClick={handleRotate}
              aria-label="Rotate image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z" />
              </svg>
            </button>,
            'close',
          ],
        }}
        render={{
          slideFooter: ({ slide }) => {
            return (
              <div className="yarl__slide_captions">
                {getGeolocation(slide.src)}
              </div>
            );
          },
          slide: ({ slide }) => {
            return (
              <div
                className="gallery-slide"
                style={{
                  transform: `rotate(${rotate}deg)`,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'transform 0.3s ease',
                }}
              >
                <img
                  src={slide.src}
                  alt={slide.alt || ''}
                  style={{
                    maxHeight: rotate % 180 !== 0 ? '80vw' : '80vh',
                    maxWidth: rotate % 180 !== 0 ? '80vh' : '80vw',
                    objectFit: 'contain',
                  }}
                />
              </div>
            );
          },
        }}
      />
    </div>
  );
};

export default Gallery;
